import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { testPlansApi } from '@/api/testPlans'
import { useTenantKey } from './useTenantKey'
import type { TestPlanFilters, CreateTestPlanRequest, PaginationParams, PushTestPlanRequest } from '@/types'

export function useTestPlans(projectId: string, pagination?: PaginationParams, filters?: TestPlanFilters) {
  const tenantKey = useTenantKey()
  const params = pagination || filters ? { ...pagination, ...filters } : undefined

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'test-plans', params),
    queryFn: () => testPlansApi.getByProject(projectId, params),
    enabled: !!projectId,
    staleTime: 0,
  })
}

export function useCreateTestPlan(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (data: CreateTestPlanRequest) => testPlansApi.create(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-plans') })
    },
  })
}

export function useDeleteTestPlan(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (testPlanId: string) => testPlansApi.delete(projectId, testPlanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-plans') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-suites') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
    },
  })
}

export function usePushTestPlanToDevOps(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ testPlanId, data }: { testPlanId: string; data: PushTestPlanRequest }) =>
      testPlansApi.pushToDevOps(projectId, testPlanId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-plans') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-suites') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-cases') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
    },
  })
}

export function useAzureDevOpsProjects(projectId: string, enabled: boolean, connectorId?: string) {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'azure-devops', 'projects', connectorId),
    queryFn: () => testPlansApi.getAzureDevOpsProjects(projectId, connectorId),
    enabled: !!projectId && enabled,
    staleTime: 60_000,
  })
}

export function useAzureDevOpsTestPlans(projectId: string, azureProject: string, connectorId?: string) {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'azure-devops', azureProject, 'test-plans', connectorId),
    queryFn: () => testPlansApi.getAzureDevOpsPlans(projectId, azureProject, connectorId),
    enabled: !!projectId && !!azureProject,
    staleTime: 60_000,
  })
}

export function useAzureDevOpsTestSuites(projectId: string, azureProject: string, azurePlanId: number | null, connectorId?: string) {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'azure-devops', azureProject, 'test-plans', azurePlanId, 'test-suites', connectorId),
    queryFn: () => testPlansApi.getAzureDevOpsSuites(projectId, azureProject, azurePlanId as number, connectorId),
    enabled: !!projectId && !!azureProject && azurePlanId != null && azurePlanId > 0,
    staleTime: 60_000,
  })
}
